import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の所作指針（route=/standards/deportment）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/hospitality と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 設立1890年（明治23年）/ 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・違反件数・罰則は書かない

export default function StandardsDeportmentPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/deportment']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の所作指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const posturePillars = [
    {
      en: 'Axis',
      ja: '軸の静けさ',
      text: '頭頂から足元へ一本の線が通る感覚を保ちます。肩を上げず、胸を過度に張らず、重心は足裏の中央やや前。静止しているときほど、力みのない垂直が品位を伝えます。'
    },
    {
      en: 'Stride',
      ja: '歩幅と着地',
      text: '歩幅は急がず、かかとから爪先へ滑らかに体重を移します。床を蹴る音、すり足の擦過音のどちらも抑え、廊下でも室内でも同じ静けさを目指します。'
    },
    {
      en: 'Turn',
      ja: '方向転換',
      text: '急な旋回は衣擦れと視線の揺れを生みます。立ち止まるか、小さな弧で向きを変えるかを場面で選び、周囲の会話や作業を乱さない軌道を選びます。'
    }
  ];

  const handForms = [
    {
      title: '空の手の収め方',
      body: '何も持たないとき、指先は自然に揃え、握りこぶしやポケットへの挿入を避けます。体側に沿わせるか、軽く重ねるかを場の改まりに合わせて選び、落ち着いた輪郭を保ちます。'
    },
    {
      title: '受け取りの基本',
      body: '相手の手元が見える位置で両手、あるいは利き手を添え、目だけを先に動かさず顔全体で受け止める姿勢をとります。重さや温度を確かめてから引き取ることで、落下や衝突を防ぎます。'
    },
    {
      title: '差し出しの基本',
      body: '取っ手や読みやすい向きを相手側へ向け、自分の指が相手の手に重ならない角度で渡します。高さは相手の腰から胸のあいだを目安にし、押しつけるのではなく「置ける余白」を残します。'
    }
  ];

  const gazeBands = [
    {
      no: '01',
      title: '視線の高さ',
      text: '依頼者や来客の目を長く射抜かず、眉間から頬のあたりを穏やかに捉えます。確認や合図が必要なときだけ目線を合わせ、用が済めば速やかに外します。'
    },
    {
      no: '02',
      title: '間合いの層',
      text: '会話・案内・給仕では必要な距離が異なります。手が届く寸前で止まる層、声だけが届く層、完全に下がる層を場面ごとに切り替え、相手の肩や書類に影を落とさない位置を守ります。'
    },
    {
      no: '03',
      title: '複数人がいる場',
      text: '誰か一人に視線が偏ると、場の公平さが崩れます。話者を尊重しつつ、他の同席者にも短い目配りを配り、特定の人だけを監視している印象を与えないよう整えます。'
    },
    {
      no: '04',
      title: '待機の目線',
      text: '控えているあいだは、相手の私語や手元の細部を観察しすぎない位置に焦点を置きます。呼ばれた瞬間にすぐ応じられるよう、顔は上げすぎず、意識だけを開いておきます。'
    }
  ];

  const doorChairNotes = [
    {
      label: '扉の開閉',
      points: [
        'ノックや一声の要否を家の流儀に合わせ、入室前に間を取る',
        '取っ手は静かに握り、開く幅は通行に必要な最小限にとどめる',
        '閉扉は最後まで手を添え、バネや風で音が立つ余地を残さない'
      ]
    },
    {
      label: '椅子の扱い',
      points: [
        '引く・戻すは床を擦らない高さで行い、背もたれを揺らさない',
        '着席の補助は相手の動作に合わせ、押し込むのではなく支える',
        '退席後は元の位置と角度を整え、次の人が迷わない状態へ戻す'
      ]
    },
    {
      label: '狭所と段差',
      points: [
        '廊下のすれ違いは先に道を譲り、背中を向けたまま通り過ぎない',
        '段差では自分の足音より、相手の安全確認を優先する',
        '荷物やトレイを持つときは視界と回転半径を先に測る'
      ]
    }
  ];

  const serviceOrientation = [
    {
      en: 'Approach',
      ja: '近づく角度',
      text: '正面から遮るように立たず、利き手側やや斜めから近づきます。会話の流れを切らない位置を選び、必要なら一言添えてから手元へ入ります。'
    },
    {
      en: 'Serve',
      ja: '差し出す向き',
      text: '器や書類の正面が相手に向くよう持ち替え、自分の袖やネクタイが卓上に触れない姿勢を保ちます。腰だけを曲げず、膝と背の分担で高さを合わせます。'
    },
    {
      en: 'Withdraw',
      ja: '下がる軌道',
      text: '用が済んだら後退してから向きを変え、相手の視界の端で静かに立ち位置を戻します。急な横切りや、会話の正面を塞ぐ退場は避けます。'
    },
    {
      en: 'Hold',
      ja: '待機の向き',
      text: '壁や家具のラインに沿い、部屋の動線を塞がない肩の向きを保ちます。呼ばれたときに最短で動けるよう、足先だけを柔らかく開いておきます。'
    }
  ];

  const soundPresence = [
    '硬質の靴音は床材に合わせて歩幅と踏み込みを調整し、鳴る箇所では速度を落とす。',
    '鍵・トレイ・食器の接触音は、置く前に一度浮かせてから静かに接地させる。',
    '衣擦れや腕時計の接触は、動作を小さくし、装飾品の選定段階から抑える。',
    '咳や返事の声量は部屋の広さと会話の大きさに合わせ、遠くへ張り上げない。',
    '「いないのにいる」安心感は、音を消すことと、必要なときだけ現れることの両立から生まれる。'
  ];

  const drillCycles = [
    {
      en: 'Observe',
      ja: '観察',
      text: '模範となる動きを断片ではなく一連の流れで見ます。どこで間が生まれ、どこで音が消えているかを言葉にしてメモします。'
    },
    {
      en: 'Isolate',
      ja: '分解',
      text: '歩行・手元・視線など要素ごとに分け、一つずつ遅速を変えて繰り返します。速い本番の真似より、遅い正確さを先に身につけます。'
    },
    {
      en: 'Integrate',
      ja: '統合',
      text: '要素を戻し、扉から案内、着席補助から給仕までを通しで行います。途中で崩れた点だけを抜き出し、再び分解へ戻します。'
    },
    {
      en: 'Review',
      ja: '点検',
      text: '第三者の短い講評や、鏡・録画による自己確認で癖を可視化します。評価は人格ではなく動作の再現性に向けます。'
    }
  ];

  const faqItems = [
    {
      q: '音を立てない歩行は、どのように練習すればよいですか？',
      a: 'まず裸足や柔らかい靴で、かかとから爪先への体重移動だけをゆっくり繰り返し、次に普段の靴で床材ごとの響きを確認します。歩幅を詰め、膝をわずかに柔らかく保つと着地音が抑えやすくなります。廊下と室内で同じ意識を使うことが要点です。'
    },
    {
      q: '物の受け渡しで両手と片手の使い分けはありますか？',
      a: '重さ・割れやすさ・改まりの度合いで選びます。器や重要書類は両手または添え手を基本とし、軽く堅牢なものでも相手の手が塞がっているときは支えを用意します。いずれの場合も、取っ手や読みやすい向きを相手側へ向けることが先です。'
    },
    {
      q: '所作の自己評価で、最初に見るべき点は何ですか？',
      a: '速さより、停止したときの軸、歩き出しの音、手元の余分な動きの三点を見ます。ここが整うと視線と間合いの修正が容易になります。Associate・Professional・Masterの各段階でも、基礎の静けさは共通して求められます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の所作指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の所作指針。所作を基準化する理由、立ち姿と歩行、手の使い方と物の受け渡し、視線と間合い、扉と椅子の扱い、給仕時の身体の向き、音と気配の管理、所作の評価と反復訓練。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/deportment'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の所作指針', item: 'https://japanbutlerassociation.com/standards/deportment' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-butler-profession-hero.jpg"
          alt="静かな室内で姿勢を整える執事。所作の基準を象徴する一枚"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Deportment Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の所作指針</span>
              <span className="block text-[#d7b779]">立ち居振る舞いの基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創設以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める所作の基準として、立ち姿と歩行、手の扱い、視線と間合い、扉と椅子、給仕時の向き、音と気配、反復訓練の考え方を示します。言葉より先に場へ届く身体の静けさを、現場で再現するための座標です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 左番号帯＋右本文（hospitalityの三指標グリッドとは別型） */}
          <section>
            <div className="grid lg:grid-cols-[200px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] px-6 py-8 text-center lg:text-left">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">
                  I. Why Form
                </span>
                <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-4xl leading-none mb-2">01</span>
                <span className="block font-['Noto_Serif_JP',serif] text-[#f8f6f0]/80 text-sm tracking-[0.08em]">基準化の理由</span>
              </div>
              <div>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  所作を<span className="text-[#a38e65]">基準化する理由</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                  <p>
                    所作は、個人の癖やその日の体調に委ねたままでは、家ごとの品位を安定して再現できません。同じ扉の開け方、同じ受け渡しの角度が続くことで、依頼者は「次も滞らない」と身体で理解します。基準化とは型を押しつけることではなく、誰が担当しても安心が途切れないための共通言語を持つことです。
                  </p>
                  <p>
                    当協会は、Associate・Professional・Masterの三段階と専門資格を通じて技能を育てると同時に、現場の身体運用の拠り所を本指針にまとめています。接遇の判断や身だしなみの整え方は
                    <a href="/standards/conduct" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事の行動規範</a>
                    や
                    <a href="/standards/grooming" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">身だしなみの基準</a>
                    と併せて参照し、基準全体の見取り図は
                    <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                    から俯瞰できます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* S2. 立ち姿と歩行＋音と気配 — 統合セクション */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Stance & Walk
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              立ち姿と歩行——音を立てない移動
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              執事の存在は、足音より先に安心として届くことが理想です。立ち姿は静止画の美しさではなく、次の一歩へ移るための準備状態。歩行は目的地へ急ぐ手段ではなく、場の空気を乱さない移動の技術です。音を立てないとは、単に小さい音で歩くことではなく、床・靴・衣・呼吸を一つの流れにまとめることです。
            </p>
            <div className="grid sm:grid-cols-3 gap-px bg-[#1a2942] border border-[#1a2942] mb-10">
              {posturePillars.map((p) => (
                <div key={p.en} className="bg-[#fbfaf7] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-become-foundation-training.jpg"
              alt="姿勢と歩行の基礎を反復して確認する研修の様子"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
            />

            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3 mt-12">
              Sound & Presence
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              音と気配の管理
            </h3>
            <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
              <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                    気配の管理とは、姿を消すことではありません。必要なときに確実に現れ、不要なときには音も影も残さない——その切替を意図的に行うことです。床材、建具、食器、衣服、声。それぞれが発する信号を個別に整え、最後に一つの静けさとして重ねます。
                  </p>
                  <ul className="space-y-4">
                    {soundPresence.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0">—</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <img
                  src="/images/generated/jba-become-master-leadership.jpg"
                  alt="場の静けさを保ちながら統括する上級執事の佇まい"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                />
              </div>
            </div>
            <p className="mt-8 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              音の問題は個人の注意不足だけで片づくものではなく、動線設計や器具の置き方、準備の段取りにも根を持ちます。Master Butlerが示す統括の視点は、個々の足音を叱る前に、鳴る構造そのものを整えるところにあります。資格の段階にかかわらず、自分の発する信号に責任を持つことが所作の倫理です。
            </p>
          </section>

          {/* S3. 手の使い方 — 横並び番号カード＋右に短い引用帯 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Hands & Transfer
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              手の使い方と物の受け渡し
            </h2>
            <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-stretch mb-10">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  手は、意思表示の多い部位です。余計な指の動き、物を置くときの最終の数センチ、受け取る直前の一瞬の間——ここに雑さが出ると、言葉が丁寧でも信頼は薄れます。受け渡しは「渡す／受け取る」の二動作ではなく、相手が次に使いやすい状態まで含めた一連の奉仕です。
                </p>
                <p>
                  熱いもの、割れやすいもの、書面や鍵のように意味の重いものでは、速度より向きと支えを優先します。自分の手元の完成度より、相手の手が迷わないことを先に設計してください。
                </p>
              </div>
              <div className="bg-[#1a2942] px-6 py-8 flex flex-col justify-center border border-[#a38e65]/40">
                <p className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm mb-3">Offer, do not impose</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#e7e2d8] leading-[1.95]">
                  差し出す手は、相手の選択を奪わない距離で止まります。置く場所を指示するのではなく、置ける余白を差し出すことが所作の礼です。
                </p>
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {handForms.map((item, idx) => (
                <div key={item.title} className="border border-[#e2ddd2] bg-white shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl mb-3 block">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{item.body}</p>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-butler-quality-control.jpg"
              alt="手元の角度と受け渡しを点検する執事の作業風景"
              className="w-full h-52 sm:h-64 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S4. 視線と間合い — 細罫リスト中心（hospitalityの好み台帳に近いが二カラム画像配置を変える） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Gaze & Distance
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              視線と間合い
            </h2>
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
              <div>
                <img
                  src="/images/generated/jba-butler-team-briefing.jpg"
                  alt="間合いと立ち位置を確認しながら打ち合せる執事たち"
                  className="w-full h-64 lg:h-[340px] object-cover border border-[#a38e65]/30 mb-6"
                />
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] border-l-2 border-[#a38e65] pl-5">
                  視線は監視ではなく、合図と敬意の通路です。間合いは壁ではなく、相手が呼吸できる余白です。どちらも「近づく勇気」と「止まる勇気」の対として鍛えます。
                </p>
              </div>
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  良い所作は、適切な距離で一度止まり、必要なときだけ視線を合わせるリズムを持っています。近すぎる配慮は圧になり、遠すぎる沈黙は冷たさに聞こえます。部屋の広さ、座席の配置、同席者の数に応じて、自分の輪郭を小さく保つ意識が欠かせません。
                </p>
                <div className="border-t border-[#1a2942]">
                  {gazeBands.map((item) => (
                    <div key={item.no} className="flex gap-5 py-5 border-b border-[#e2ddd2]">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">{item.no}</span>
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* S5. 扉と椅子 — 三列縦カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Doors & Chairs
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              扉と椅子の扱い
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                扉と椅子は、家の境界と着席の尊厳を身体で示す装置です。開閉の音一つ、椅子を引く軌跡一つが、その場の改まりを決めます。派手な丁寧さより、最後まで手を離さない完遂が品位につながります。
              </p>
              <p>
                相手が高齢者か、荷物を持っているか、会話の途中かによって補助の度合いは変わります。先回りして奪わず、求められた瞬間に過不足なく支える——その判断は接遇の境界感覚と一体です。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {doorChairNotes.map((g) => (
                <div key={g.label} className="bg-[#fbfaf7] border border-[#e2ddd2] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#a38e65]/50">{g.label}</h3>
                  <ul className="space-y-3">
                    {g.points.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-become-professional-practice.jpg"
              alt="扉と動線を意識しながら案内の実践を行う執事"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S6. 給仕時の身体の向き — 横四ステップ（枠線分割） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Orientation in Service
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              給仕時の身体の向き
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              給仕の成否は、料理や器物の扱い以前に、身体がどちらを向いているかで決まります。会話の正面を塞ぐ、利き手と逆から無理に差し出す、下がるときに相手の視界を横切る——向きの設計が甘いと、どれほど静かな手でも場は乱れます。
            </p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#a38e65]">
              {serviceOrientation.map((s) => (
                <li key={s.en} className="border-b sm:border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6 bg-[#fbfaf7]">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-3">{s.ja}</span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                </li>
              ))}
            </ol>
            <div className="mt-10 border border-[#e2ddd2] bg-white p-7 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                卓上に複数の器があるときは、自分の肩口が隣席の会話へ割り込まない軌道を先に描きます。左から出すか右から出すかの家の流儀がある場合はそれに従い、流儀が明示されていないときは相手の利き手と席の空きを観察して決めます。向きは一度決めたら途中で迷わず、迷いが生まれたら一度下がって立て直します。
              </p>
            </div>
          </section>

          {/* S7. 評価と反復訓練 — 四サイクル＋補足 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Drill & Review
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              所作の評価と反復訓練
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              所作は一度学べば終わりません。家が変わり、季節が変わり、自分の身体条件が変われば、基準は再び身体に入れ直す必要があります。評価は減点のための儀式ではなく、再現性を高めるための点検です。速さ・派手さ・自己流の工夫より、静けさと完遂をものさしに置きます。
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
              {drillCycles.map((d) => (
                <div key={d.en} className="border border-[#e2ddd2] bg-white px-5 py-6 shadow-[0_12px_40px_rgba(26,41,66,0.06)]">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{d.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{d.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{d.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#fbfaf7] border-l-4 border-[#a38e65] px-6 sm:px-8 py-6">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                訓練の記録は、失敗の羅列ではなく「どの要素を、どの遅速で、どこまで再現できたか」を残します。引き継ぎや休暇明けには、家固有の扉の癖や床の鳴る位置など、現場固有の条件を優先して再確認します。評価者は人格を語らず、動作と結果だけを短く返します。
              </p>
            </div>
          </section>

          {/* S8. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S9. 関連導線 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Related Paths
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h3>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                所作指針は、身体の運用だけを切り離して完結するものではありません。行動の規範と身だしなみの基準をあわせて整え、資格制度を通じて判断力を段階的に育てることで、現場での再現性が生まれます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の静けさを示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards" className={BTN08_ON_NAVY}>
                  基準・指針の一覧
                </a>
                <a href="/standards/grooming" className={BTN08_ON_NAVY}>
                  身だしなみの基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards/conduct" className={BTN08_ON_LIGHT}>
                  執事の行動規範
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
